import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    ListItemText,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MuiTelInput } from 'mui-tel-input';
import React, { useCallback } from 'react';
import { dayjs } from '../utils/dayjs';
import StyledTextField from './StyledTextField';

const categorySelectSx = {
    minWidth: 0,
    '& .MuiSelect-select': {
        minHeight: '28px !important',
        padding: '12px 32px 0 0 !important',
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    '& .MuiSelect-icon': {
        right: '12px',
    }
};

const categoryGroupLabelSx = {
    display: 'block',
    m: 0,
    p: 0,
    mt: 0,
    mb: 0.25,
    textAlign: 'left',
    color: '#999999',
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: 400,
};

const categoryOptionLabelSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '24px',
    color: '#000000',
};

const categoryControlLabelSx = {
    width: '100%',
    mr: 0,
    '& .MuiFormControlLabel-label': {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
    },
};

const columnSx = {
    flex: 1,
    minWidth: 0,
};

const isFieldValueEmpty = (field, formData) => {
    const value = formData[field.id];
    if (field.type === 'tag_category' || field.type === 'category') {
        return !Array.isArray(value) || value.length === 0;
    }
    if (field.type === 'boolean') {
        return !value;
    }
    return value === undefined || value === null || value.toString().trim() === '';
};

// Multiline and full width: the standard custom_note and every long-text (`text`) field
const isMultilineField = (field) => field.id === 'custom_note' || field.type === 'text';

const DynamicFields = React.memo(({ fields, formData, onChange, disabled, validationErrors = {}, touchedFields = {}, onFieldBlur }) => {
    const theme = useTheme();
    const isWideLayout = useMediaQuery(theme.breakpoints.up('lg'));
    const isMinor = formData.is_minor || false;
    const CATEGORY_RADIO_THRESHOLD = 3;
    const CATEGORY_MULTISELECT_THRESHOLD = 4;

    const markTouched = useCallback((fieldId) => {
        if (onFieldBlur) {
            onFieldBlur(fieldId);
        }
    }, [onFieldBlur]);

    const getCategorySummary = (selectedValues, options) => {
        if (selectedValues.length === 0) {
            return '';
        }

        const selectedLabels = options
            .filter(option => selectedValues.includes(option.id))
            .map(option => option.friendly_name || option.name);

        if (selectedLabels.length <= 2) {
            return selectedLabels.join(', ');
        }

        return `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;
    };

    const renderField = useCallback((field) => {
        const isRequired = field.required || false;
        const fieldValue = formData[field.id] || '';
        const isEmpty = isFieldValueEmpty(field, formData);
        const isTouched = !!touchedFields[field.id];
        const showRequired = isRequired && isTouched && isEmpty && !validationErrors[field.id];
        const requiredOutlineSx = showRequired
            ? {
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F35' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F35' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F35' }
            }
            : {};

        // Handle category type fields (previously tag_category)
        if (field.type === 'category' || field.type === 'tag_category') {
            const selectedValues = formData[field.id] || [];
            const options = field.options || [];
            const isMultiselect = field.multiselect === true;
            const useSingleSelectDropdown = !isMultiselect && options.length >= CATEGORY_RADIO_THRESHOLD;
            const useMultiselectDropdown = isMultiselect && options.length >= CATEGORY_MULTISELECT_THRESHOLD;
            const useDropdown = useSingleSelectDropdown || useMultiselectDropdown;

            return (
                <Box key={field.id} sx={{ minWidth: 0, width: '100%' }}>
                    <Box
                        sx={{
                            minWidth: 0,
                            width: '100%',
                            ...(useDropdown
                                ? {}
                                : {
                                    border: '1px solid #EAECF0',
                                    borderRadius: '12px',
                                    backgroundColor: '#FFFFFF',
                                    px: 2,
                                    pt: 1,
                                    pb: 1.5
                                }),
                            ...((showRequired || validationErrors[field.id]) && !useDropdown
                                ? { borderColor: '#FE3F35' }
                                : {})
                        }}
                    >
                        {isMultiselect ? (
                            <FormGroup onBlur={!useMultiselectDropdown ? () => markTouched(field.id) : undefined}>
                                {!useMultiselectDropdown && (
                                    <FormLabel component="legend" sx={categoryGroupLabelSx}>
                                        {field.name}{isRequired ? ' *' : ''}
                                    </FormLabel>
                                )}
                                {useMultiselectDropdown ? (
                                    <StyledTextField
                                        fullWidth
                                        select
                                        name={field.id}
                                        label={field.name}
                                        value={selectedValues}
                                        onChange={onChange}
                                        onBlur={() => markTouched(field.id)}
                                        disabled={disabled}
                                        error={!!validationErrors[field.id]}
                                        helperText={validationErrors[field.id]}
                                        sx={{ ...requiredOutlineSx, ...categorySelectSx }}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => getCategorySummary(selected, options),
                                            onClose: () => markTouched(field.id)
                                        }}
                                    >
                                        {options.map((option) => {
                                            const optionId = option.id;
                                            const optionName = option.name;
                                            const displayName = option.friendly_name || optionName;

                                            return (
                                                <MenuItem key={optionId} value={optionId}>
                                                    <Checkbox checked={selectedValues.includes(optionId)} />
                                                    <ListItemText primary={displayName} />
                                                </MenuItem>
                                            );
                                        })}
                                    </StyledTextField>
                                ) : (
                                    options.map((option) => {
                                        const optionId = option.id;
                                        const optionName = option.name;
                                        const displayName = option.friendly_name || optionName;

                                        return (
                                            <FormControlLabel
                                                key={optionId}
                                                name={field.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedValues.includes(optionId) || false}
                                                        onChange={onChange}
                                                        name={`${field.id}-${optionId}`}
                                                        disabled={disabled}
                                                        sx={{ py: 0.5 }}
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        title={displayName}
                                                        sx={categoryOptionLabelSx}
                                                    >
                                                        {displayName}
                                                    </Typography>
                                                }
                                                sx={categoryControlLabelSx}
                                            />
                                        );
                                    })
                                )}
                                {!useMultiselectDropdown && validationErrors[field.id] && (
                                    <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                        {validationErrors[field.id]}
                                    </Typography>
                                )}
                            </FormGroup>
                        ) : useSingleSelectDropdown ? (
                            <StyledTextField
                                fullWidth
                                select
                                name={field.id}
                                label={field.name}
                                value={selectedValues.length > 0 ? selectedValues[0] : ''}
                                onChange={onChange}
                                onBlur={() => markTouched(field.id)}
                                disabled={disabled}
                                error={!!validationErrors[field.id]}
                                helperText={validationErrors[field.id]}
                                sx={{ ...requiredOutlineSx, ...categorySelectSx }}
                                SelectProps={{
                                    onClose: () => markTouched(field.id)
                                }}
                            >
                                {options.map((option) => {
                                    const optionId = option.id;
                                    const optionName = option.name;
                                    const displayName = option.friendly_name || optionName;

                                    return (
                                        <MenuItem key={optionId} value={optionId}>
                                            {displayName}
                                        </MenuItem>
                                    );
                                })}
                            </StyledTextField>
                        ) : (
                            <RadioGroup
                                value={selectedValues.length > 0 ? selectedValues[0] : ''}
                                onChange={onChange}
                                onBlur={() => markTouched(field.id)}
                                name={field.id}
                            >
                                <FormLabel component="legend" sx={categoryGroupLabelSx}>
                                    {field.name}{isRequired ? ' *' : ''}
                                </FormLabel>
                                {options.map((option) => {
                                    const optionId = option.id;
                                    const optionName = option.name;
                                    const displayName = option.friendly_name || optionName;

                                    return (
                                        <FormControlLabel
                                            key={optionId}
                                            value={optionId}
                                            control={
                                                <Radio
                                                    disabled={disabled}
                                                    sx={{ py: 0.5 }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    title={displayName}
                                                    sx={categoryOptionLabelSx}
                                                >
                                                    {displayName}
                                                </Typography>
                                            }
                                            sx={categoryControlLabelSx}
                                        />
                                    );
                                })}
                                {validationErrors[field.id] && (
                                    <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                        {validationErrors[field.id]}
                                    </Typography>
                                )}
                            </RadioGroup>
                        )}
                    </Box>
                    {showRequired && (
                        <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                            * Required
                        </Typography>
                    )}
                </Box>
            );
        }

        // Handle boolean type fields
        if (field.type === 'boolean') {
            return (
                <Box key={field.id}>
                    <Box
                        sx={{
                            ...((showRequired || validationErrors[field.id])
                                ? { borderColor: '#FE3F35', borderWidth: '1px', borderStyle: 'solid', borderRadius: '12px', px: 1 }
                                : {})
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!fieldValue}
                                    onChange={onChange}
                                    onBlur={() => markTouched(field.id)}
                                    name={field.id}
                                    disabled={disabled}
                                />
                            }
                            label={field.id === 'is_minor' ? 'This person is a minor' : field.name}
                            sx={{ width: '100%' }}
                        />
                        {validationErrors[field.id] && (
                            <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                {validationErrors[field.id]}
                            </Typography>
                        )}
                        {showRequired && (
                            <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                * Required
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        // Handle number type fields
        if (field.type === 'number') {
            return (
                <Box key={field.id}>
                    <Box>
                        <StyledTextField
                            fullWidth
                            name={field.id}
                            label={field.name}
                            value={fieldValue}
                            onChange={onChange}
                            onBlur={() => markTouched(field.id)}
                            disabled={disabled}
                            variant="outlined"
                            type="number"
                            autoComplete="off"
                            error={!!validationErrors[field.id]}
                            helperText={validationErrors[field.id]}
                            sx={requiredOutlineSx}
                        />
                        {showRequired && (
                            <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                * Required
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        // Date fields (date_of_birth and any date-typed custom field) use the DatePicker
        if (field.id === 'date_of_birth' || field.type === 'date') {
            const dateValue = fieldValue ? dayjs(fieldValue) : null;

            return (
                <Box key={field.id}>
                    <Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={field.name}
                                value={dateValue}
                                onChange={(newValue) => {
                                    const event = {
                                        target: {
                                            name: field.id,
                                            value: newValue ? newValue.format('YYYY-MM-DD') : ''
                                        }
                                    };
                                    onChange(event);
                                }}
                                disabled={disabled}
                                slots={{
                                    textField: StyledTextField
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        autoComplete: 'off',
                                        error: !!validationErrors[field.id],
                                        helperText: validationErrors[field.id],
                                        placeholder: '2000-01-01',
                                        sx: requiredOutlineSx,
                                        onBlur: () => markTouched(field.id)
                                    }
                                }}
                                format="YYYY-MM-DD"
                            />
                        </LocalizationProvider>
                        {showRequired && (
                            <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                * Required
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        // Handle phone field specifically with MuiTelInput
        if (field.id === 'phone') {
            const shouldHideForMinor = isMinor;

            return (
                <Box key={field.id} sx={{ display: shouldHideForMinor ? 'none' : 'block' }}>
                    <Box>
                        <MuiTelInput
                            fullWidth
                            name={field.id}
                            label={field.name}
                            value={fieldValue || ''}
                            onChange={(value) => {
                                const event = {
                                    target: {
                                        name: field.id,
                                        value: value
                                    }
                                };
                                onChange(event);
                            }}
                            onBlur={() => markTouched(field.id)}
                            disabled={disabled}
                            defaultCountry="US"
                            preferredCountries={['US', 'CA', 'GB', 'AU']}
                            variant="outlined"
                            autoComplete="off"
                            error={!!validationErrors[field.id]}
                            helperText={validationErrors[field.id] || ''}
                            sx={{
                                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#EAECF0'
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#EAECF0'
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#EAECF0'
                                },
                                '& .MuiOutlinedInput-root.Mui-focused:not(:hover) .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#EAECF0'
                                },
                                ...requiredOutlineSx
                            }}
                        />
                        {showRequired && (
                            <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                                * Required
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        // Handle string type fields and specific field cases
        const getFieldType = () => {
            if (field.id === 'email') return 'email';
            return 'text';
        };

        const shouldHideForMinor = field.id === 'email' && isMinor;
        // Long text: custom_note and text-typed custom fields
        const isMultiline = isMultilineField(field);

        return (
            <Box key={field.id} sx={{ display: shouldHideForMinor ? 'none' : 'block' }}>
                <Box>
                    <StyledTextField
                        fullWidth
                        name={field.id}
                        label={field.name}
                        value={fieldValue}
                        onChange={onChange}
                        onBlur={() => markTouched(field.id)}
                        disabled={disabled}
                        variant="outlined"
                        type={getFieldType()}
                        autoComplete="off"
                        multiline={isMultiline}
                        rows={isMultiline ? 3 : undefined}
                        error={!!validationErrors[field.id]}
                        helperText={validationErrors[field.id]}
                        sx={requiredOutlineSx}
                        slotProps={{
                            inputLabel: {
                                sx: {
                                    // paddingTop: field.id === 'custom_note' ? '-16px' : undefined
                                }
                            }
                        }}
                    />
                    {showRequired && (
                        <Typography variant="caption" sx={{ color: '#FE3F35', fontSize: '12px', mt: 0.5, pl: 2, display: 'block' }}>
                            {field.id === 'email' ? '* Email cannot be blank' : '* Required'}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }, [formData, onChange, disabled, isMinor, validationErrors, touchedFields, markTouched]);

    const isFieldHidden = useCallback((field) => {
        return (field.id === 'email' || field.id === 'phone') && isMinor;
    }, [isMinor]);

    const isFullWidthField = useCallback((field) => {
        if (field.type === 'boolean') {
            return true;
        }

        return isMultilineField(field);
    }, []);

    if (!fields || !Array.isArray(fields)) {
        return null;
    }

    const visibleFields = fields.filter(field => !isFieldHidden(field));
    const layoutSections = [];
    let currentHalfWidthSection = [];

    visibleFields.forEach((field) => {
        if (isFullWidthField(field)) {
            if (currentHalfWidthSection.length > 0) {
                layoutSections.push({ type: 'columns', fields: currentHalfWidthSection });
                currentHalfWidthSection = [];
            }

            layoutSections.push({ type: 'full', field });
            return;
        }

        currentHalfWidthSection.push(field);
    });

    if (currentHalfWidthSection.length > 0) {
        layoutSections.push({ type: 'columns', fields: currentHalfWidthSection });
    }

    if (!isWideLayout) {
        return (
            <Box>
                {visibleFields.map((field) => (
                    <Box key={field.id} sx={{ mb: 3 }}>
                        {renderField(field)}
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {layoutSections.map((section, sectionIndex) => {
                if (section.type === 'full') {
                    return (
                        <Box key={`full-${section.field.id}-${sectionIndex}`}>
                            {renderField(section.field)}
                        </Box>
                    );
                }

                const leftColumnFields = section.fields.filter((_, index) => index % 2 === 0);
                const rightColumnFields = section.fields.filter((_, index) => index % 2 === 1);

                return (
                    <Box key={`columns-${sectionIndex}`} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
                        <Box sx={columnSx}>
                            {leftColumnFields.map((field, index) => (
                                <Box key={field.id} sx={{ mb: index === leftColumnFields.length - 1 ? 0 : 3, minWidth: 0 }}>
                                    {renderField(field)}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={columnSx}>
                            {rightColumnFields.map((field, index) => (
                                <Box key={field.id} sx={{ mb: index === rightColumnFields.length - 1 ? 0 : 3, minWidth: 0 }}>
                                    {renderField(field)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
});

DynamicFields.displayName = 'DynamicFields';

export default DynamicFields; 
